import React, { useState } from "react";
import { Form, Card, Row, Col, Button, Table } from "react-bootstrap";

function BakersPercentageCalculator() {
  const [flourWeight, setFlourWeight] = useState("");
  const [ingredients, setIngredients] = useState([
    { name: "Water", weight: "", isRequired: true },
    { name: "Salt", weight: "", isRequired: true },
    { name: "Starter", weight: "", isRequired: true },
  ]);

  const calculatePercentage = (ingredientWeight) => {
    if (!flourWeight || !ingredientWeight) return null;
    return (
      (parseFloat(ingredientWeight) / parseFloat(flourWeight)) *
      100
    ).toFixed(1);
  };

  const handleIngredientChange = (index, field, value) => {
    const newIngredients = [...ingredients];
    newIngredients[index] = { ...newIngredients[index], [field]: value };
    setIngredients(newIngredients);
  };

  const addIngredient = () => {
    setIngredients([
      ...ingredients,
      { name: "", weight: "", isRequired: false },
    ]);
  };

  const removeIngredient = (index) => {
    if (!ingredients[index].isRequired) {
      setIngredients(ingredients.filter((_, i) => i !== index));
    }
  };

  const getTotalWeight = () => {
    if (!flourWeight) return null;
    const total =
      parseFloat(flourWeight) +
      ingredients.reduce((sum, ing) => {
        return sum + (parseFloat(ing.weight) || 0);
      }, 0);
    return total.toFixed(0);
  };

  return (
    <div>
      <h2 className="mb-4">Baker's Percentage Calculator</h2>
      <Card className="mb-4">
        <Card.Body>
          <p className="mb-3">
            Calculate baker's percentages for your recipe. In baker's math,
            flour is always 100%, and all other ingredients are expressed as
            percentages relative to the flour weight.
          </p>

          <Form>
            <Row className="mb-4">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>
                    <strong>Flour Weight (g)</strong>
                  </Form.Label>
                  <Form.Control
                    type="number"
                    value={flourWeight}
                    onChange={(e) => setFlourWeight(e.target.value)}
                    placeholder="e.g., 1000"
                  />
                  <Form.Text className="text-muted">
                    This is your 100% reference weight
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>

            <Table responsive>
              <thead>
                <tr>
                  <th>Ingredient</th>
                  <th>Weight (g)</th>
                  <th>Baker's %</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {ingredients.map((ingredient, index) => (
                  <tr key={index}>
                    <td>
                      {ingredient.isRequired ? (
                        ingredient.name
                      ) : (
                        <Form.Control
                          type="text"
                          value={ingredient.name}
                          onChange={(e) =>
                            handleIngredientChange(
                              index,
                              "name",
                              e.target.value
                            )
                          }
                          placeholder="Ingredient name"
                        />
                      )}
                    </td>
                    <td>
                      <Form.Control
                        type="number"
                        value={ingredient.weight}
                        onChange={(e) =>
                          handleIngredientChange(
                            index,
                            "weight",
                            e.target.value
                          )
                        }
                        placeholder="Weight in grams"
                      />
                    </td>
                    <td>
                      {calculatePercentage(ingredient.weight)
                        ? `${calculatePercentage(ingredient.weight)}%`
                        : "-"}
                    </td>
                    <td>
                      {!ingredient.isRequired && (
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => removeIngredient(index)}
                        >
                          ✕
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
              {flourWeight && (
                <tfoot>
                  <tr>
                    <td>
                      <strong>Total Weight</strong>
                    </td>
                    <td>
                      <strong>{getTotalWeight()}g</strong>
                    </td>
                    <td></td>
                    <td></td>
                  </tr>
                </tfoot>
              )}
            </Table>

            <Button variant="outline-primary" onClick={addIngredient}>
              + Add Ingredient
            </Button>
          </Form>
        </Card.Body>
      </Card>

      {flourWeight && (
        <Card>
          <Card.Body>
            <h4 className="mb-3">Recipe Analysis</h4>
            <p className="mb-2">
              Your recipe uses {flourWeight}g of flour as the base (100%).
              {ingredients.some(
                (ing) => ing.weight && calculatePercentage(ing.weight)
              ) && (
                <>
                  {" "}
                  Key ratios:{" "}
                  {ingredients
                    .filter((ing) => ing.weight && ing.isRequired)
                    .map((ing, index) => (
                      <span key={index} className="fw-medium">
                        {ing.name}: {calculatePercentage(ing.weight)}%
                        {index <
                        ingredients.filter((i) => i.weight && i.isRequired)
                          .length -
                          1
                          ? ", "
                          : ""}
                      </span>
                    ))}
                </>
              )}
            </p>
            <div className="text-body-secondary">
              Total dough weight: {getTotalWeight()}g
            </div>
          </Card.Body>
        </Card>
      )}
    </div>
  );
}

export default BakersPercentageCalculator;
